import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { dashboardService } from '../../../../services/api/dashboardService';
import { donationService } from '../../../../services/api/donationService';
import { eventService } from '../../../../services/api/eventService';
import { volunteerService } from '../../../../services/api/volunteerService';
import { Metrics } from '../../../../types/metrics';
import { formatTimeAgo } from '../utils/formatters';
import { useDonations } from '../../../../hooks/useDonations';
import { useDonors } from '../../../../components/Admin/hooks/useDonors';

// Types
export interface ActivityItem {
  id: string;
  type: 'donation' | 'volunteer' | 'event';
  name: string;
  action: string;
  amount?: string;
  timestamp: string;
  date: Date; // For sorting
}

export interface GoalItem {
  name: string;
  current: number;
  target: number;
  unit: string;
}

// Interface for donation data
interface Donation {
  id: string;
  amount: number;
  createdAt: string;
  donorId: string;
  status: string;
  frequency?: string;
  currency?: string;
  updatedAt?: string;
}

// Interface for donor data from donations
interface DonorFromDonation {
  id: string;
  name: string;
  lastDonationAmount: number;
  lastDonationDate: string;
  totalDonations?: number;
  donationCount?: number;
}

// Helper function to standardize date parsing
const parseDate = (dateString: string): Date => {
  // Try to parse date in various formats
  const date = new Date(dateString);
  
  // Check if the date is valid
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  // Try to parse M/D/YYYY format
  const parts = dateString.split('/');
  if (parts.length === 3) {
    const month = parseInt(parts[0]) - 1; // months are 0-indexed in JS
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    return new Date(year, month, day);
  }
  
  console.error(`Failed to parse date: ${dateString}`);
  return new Date(0); // Return epoch as fallback
};

export const useDashboardData = () => {
  const { tokens } = useAuth();

  // Metrics state
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use useDonations hook to get donation data
  const { donations: allDonations, loading: donationsLoading } = useDonations({
    perPage: 100 // Get more donations to ensure we have enough data
  });
  
  // Use useDonors hook to get donor information
  const { donors, loading: donorsLoading } = useDonors();
  
  // Additional data states
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);
  const [recentVolunteers, setRecentVolunteers] = useState<any[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);

  // Helper function to get donor name from donorId
  const getDonorName = (donorId: string): string => {
    // Check if donorId is undefined or null
    if (!donorId) {
      return 'Unknown Donor';
    }
    
    const donor = donors.find(d => d.id === donorId);
    if (donor) {
      // Return actual donor name if found
      return donor.name || `${donor.firstName || ''} ${donor.lastName || ''}`.trim() || `Donor-${donorId.substring(0, 8)}`;
    }
    // Fallback to the ID-based name if donor not found
    return `Donor-${donorId.substring(0, 8)}`;
  };

  // Derive recent donations by donor (including duplicates for multiple donations)
  const recentDonorDonations = useMemo(() => {
    // Process only successful donations
    const successfulDonations = allDonations.filter(donation => 
      donation && donation.status === 'succeeded'
    );
    
    // Map each donation to a donor-centric view
    const donorDonations = successfulDonations.map(donation => {
      if (!donation || !donation.donorId) return null;
      
      return {
        id: `${donation.id}-${donation.donorId}`,
        donorId: donation.donorId,
        name: getDonorName(donation.donorId), // Use actual donor name
        lastDonationAmount: donation.amount || 0, // Default to 0 if amount is undefined
        lastDonationDate: donation.createdAt,
        donationCount: 1, // Each entry represents one donation
      };
    }).filter(Boolean) as DonorFromDonation[];
    
    // Sort by date (newest first) and take the 10 most recent donations
    return donorDonations
      .sort((a, b) => {
        const dateA = parseDate(a.lastDonationDate).getTime();
        const dateB = parseDate(b.lastDonationDate).getTime();
        return dateB - dateA;
      })
      .slice(0, 10);
  }, [allDonations, donors]);

  // Original recentDonors logic - unique donors with their latest donation
  const recentDonors = useMemo(() => {
    // Map to track unique donors by ID
    const donorsMap = new Map<string, DonorFromDonation>();
    
    // Process only successful donations
    const successfulDonations = allDonations.filter(donation => 
      donation && donation.status === 'succeeded'
    );
    
    // Create donor objects from donations
    successfulDonations.forEach(donation => {
      if (!donation || !donation.donorId) return;
      
      // Parse dates for proper comparison
      const donationDate = parseDate(donation.createdAt);
      
      // Get donation amount with fallback to 0
      const donationAmount = donation.amount || 0;
      
      // If this donor isn't in our map yet, add them
      if (!donorsMap.has(donation.donorId)) {
        donorsMap.set(donation.donorId, {
          id: donation.donorId,
          name: getDonorName(donation.donorId), // Use actual donor name
          lastDonationAmount: donationAmount,
          lastDonationDate: donation.createdAt,
          donationCount: 1,
          totalDonations: donationAmount
        });
      } else {
        // Update existing donor record
        const existingDonor = donorsMap.get(donation.donorId)!;
        const existingDate = parseDate(existingDonor.lastDonationDate);
        
        // Update last donation date if this one is newer
        if (donationDate > existingDate) {
          existingDonor.lastDonationDate = donation.createdAt;
          existingDonor.lastDonationAmount = donationAmount;
        }
        
        // Update totals
        existingDonor.donationCount = (existingDonor.donationCount || 0) + 1;
        existingDonor.totalDonations = (existingDonor.totalDonations || 0) + donationAmount;
        
        donorsMap.set(donation.donorId, existingDonor);
      }
    });
    
    // Convert map to array and sort by most recent donation
    const sortedDonors = Array.from(donorsMap.values())
      .sort((a, b) => {
        const dateA = parseDate(a.lastDonationDate).getTime();
        const dateB = parseDate(b.lastDonationDate).getTime();
        return dateB - dateA; // Sort by most recent first
      })
      .slice(0, 10); // Take top 10 most recent donors
      
    return sortedDonors;
  }, [allDonations, donors]);

  // Update recentDonations when allDonations changes
  useEffect(() => {
    // Filter and sort the donations (most recent first)
    const sorted = [...allDonations]
      .filter(donation => donation && donation.status === 'succeeded')
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 20); // Keep only the 20 most recent
    
    setRecentDonations(sorted);
  }, [allDonations]);

  // Load dashboard metrics
  useEffect(() => {
    const loadMetrics = async () => {
      if (!tokens?.idToken) {
        setError("Authentication token not found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        const response = await dashboardService.getDashboardMetrics(tokens.idToken);
        if (response.success) {
          setMetrics(response.metrics as unknown as Metrics);
        } else {
          setError("Failed to fetch metrics.");
        }
      } catch (error: any) {
        console.error("Failed to fetch metrics:", error);
        setError(error.message || "An error occurred while fetching metrics.");
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [tokens]);

  // Load recent volunteers
  useEffect(() => {
    const loadVolunteers = async () => {
      if (!tokens?.accessToken) return;

      try {
        const response = await volunteerService.getVolunteers(tokens.accessToken);
        if ('volunteers' in response && Array.isArray(response.volunteers)) {
          // Sort by createdAt date, newest first
          const sortedVolunteers = [...response.volunteers].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setRecentVolunteers(sortedVolunteers.slice(0, 10)); // Take most recent 10
        } else if (Array.isArray(response)) {
          // Handle case where response is already an array (for backward compatibility)
          const sortedVolunteers = [...response].sort((a, b) => 
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setRecentVolunteers(sortedVolunteers.slice(0, 10));
        } else {
          console.warn("Unexpected volunteer data format:", response);
        }
      } catch (error) {
        console.error("Failed to fetch recent volunteers:", error);
      }
    };

    loadVolunteers();
  }, [tokens]);

  // Load upcoming events
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const events = await eventService.getEvents();
        if (Array.isArray(events)) {
          // Filter upcoming events and sort by date
          const upcoming = events
            .filter(event => event.status === "upcoming")
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          setUpcomingEvents(upcoming.slice(0, 5)); // Take closest 5 upcoming events
        }
      } catch (error) {
        console.error("Failed to fetch upcoming events:", error);
      }
    };

    loadEvents();
  }, []);

  // Combine all recent activities into a unified timeline
  useEffect(() => {
    const combinedActivities: ActivityItem[] = [];

    // Add donations to activity timeline - ONLY succeeded donations
    recentDonations.filter(donation => donation.status === 'succeeded').forEach(donation => {
      // Create a unique ID if one doesn't exist
      const id = donation.id || `donation-${Date.now()}-${Math.random()}`;
      
      // Skip donations without donorId or add with placeholder
      if (!donation.donorId) {
        console.warn('Found donation without donorId:', donation);
        // Either skip this donation or add with a placeholder
        // Also ensure createdAt exists
        if (!donation.createdAt) {
          console.warn('Donation missing createdAt timestamp:', donation);
          return; // Skip donations without timestamps
        }
        
        combinedActivities.push({
          id,
          type: 'donation',
          name: 'Unknown Donor',
          action: donation.frequency === 'recurring' ? 'set up recurring donation' : 'donated',
          amount: donation.amount !== undefined ? `$${donation.amount.toLocaleString()}` : 'Unknown amount',
          timestamp: formatTimeAgo(new Date(donation.createdAt)),
          date: new Date(donation.createdAt)
        });
        return; // Skip to next donation
      }
      
      // Skip donations without timestamps
      if (!donation.createdAt) {
        console.warn('Donation missing createdAt timestamp:', donation);
        return;
      }
      
      // Use actual donor name
      const donorName = getDonorName(donation.donorId);
      
      combinedActivities.push({
        id,
        type: 'donation',
        name: donorName,
        action: donation.frequency === 'recurring' ? 'set up recurring donation' : 'donated',
        amount: donation.amount !== undefined ? `$${donation.amount.toLocaleString()}` : 'Unknown amount',
        timestamp: formatTimeAgo(new Date(donation.createdAt)),
        date: new Date(donation.createdAt)
      });
    });

    // Add volunteers to activity timeline
    recentVolunteers.forEach(volunteer => {
      if (volunteer && volunteer.id) {
        // Skip volunteers without timestamps
        if (!volunteer.createdAt) {
          console.warn('Volunteer missing createdAt timestamp:', volunteer);
          return;
        }
        
        // Extract the name - handle different potential formats
        const name = volunteer.name || 
                    (volunteer.firstName ? 
                      volunteer.firstName + (volunteer.lastName ? ` ${volunteer.lastName}` : '') : 
                      'Anonymous Volunteer');
        
        combinedActivities.push({
          id: volunteer.id,
          type: 'volunteer',
          name,
          action: 'signed up',
          timestamp: formatTimeAgo(new Date(volunteer.createdAt)),
          date: new Date(volunteer.createdAt)
        });
      }
    });

    // Add recent events to activity timeline
    upcomingEvents.forEach(event => {
      if (event && event.id) {
        // Skip events without timestamps
        if (!event.createdAt) {
          console.warn('Event missing createdAt timestamp:', event);
          return;
        }
        
        combinedActivities.push({
          id: event.id,
          type: 'event',
          name: event.title || 'Unnamed Event',
          action: 'was scheduled',
          timestamp: formatTimeAgo(new Date(event.createdAt)),
          date: new Date(event.createdAt)
        });
      }
    });

    // Sort by date, newest first
    combinedActivities.sort((a, b) => b.date.getTime() - a.date.getTime());
    
    // Remove duplicates based on ID
    const uniqueActivities = combinedActivities.filter(
      (activity, index, self) => 
        index === self.findIndex(a => a.id === activity.id)
    );
    
    // Set activity items state (limit to 10 most recent)
    setActivityItems(uniqueActivities.slice(0, 10));
  }, [recentDonations, recentVolunteers, upcomingEvents, donors]);

  // Create performance goals based on actual metrics
  const performanceGoals = useMemo(() => {
    const goals: GoalItem[] = [];
    
    // Only add goals if metrics data is available
    if (metrics) {
      // Donation goal - assuming target is 20% higher than current
      const donationTarget = Math.round((metrics.totalDonations || 0) * 1.2);
      goals.push({
        name: 'Donation Goal',
        current: metrics.totalDonations || 0,
        target: donationTarget,
        unit: '$'
      });
      
      // Volunteer signup goal - assuming target is 15% higher than current
      const volunteerTarget = Math.round((metrics.totalVolunteers || 0) * 1.15);
      goals.push({
        name: 'Volunteer Sign-ups',
        current: metrics.totalVolunteers || 0,
        target: volunteerTarget,
        unit: ''
      });
      
      // Recurring donors goal - assuming target is 10% higher than current
      const recurringTarget = Math.round((metrics.repeatDonors || 0) * 1.1);
      goals.push({
        name: 'Recurring Donors',
        current: metrics.repeatDonors || 0,
        target: recurringTarget,
        unit: ''
      });
    }
    
    return goals;
  }, [metrics]);

  // Calculate growth rates
  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return 100;
    return ((current - previous) / previous) * 100;
  };

  // Growth calculations
  const donationGrowth = useMemo(() => {
    if (!metrics) return 0;
    
    return metrics.donationTrends && metrics.donationTrends.length >= 2 
      ? calculateGrowth(
          metrics.donationTrends[metrics.donationTrends.length - 1].value,
          metrics.donationTrends[metrics.donationTrends.length - 2].value
        ) 
      : calculateGrowth(metrics.totalDonations || 0, (metrics.totalDonations || 0) * 0.9);
  }, [metrics]);

  const donorGrowth = useMemo(() => {
    if (!metrics) return 0;
    
    return metrics.majorDonors && metrics.totalDonors
      ? calculateGrowth(metrics.totalDonors, metrics.totalDonors - metrics.majorDonors)
      : calculateGrowth(metrics.totalDonors || 0, (metrics.totalDonors || 0) * 0.95);
  }, [metrics]);

  const volunteerGrowth = useMemo(() => {
    if (!metrics) return 15; // Default growth rate
    
    return calculateGrowth(metrics.totalVolunteers || 0, (metrics.totalVolunteers || 0) * 0.85);
  }, [metrics]);

  return {
    metrics,
    recentDonations,
    recentDonors,
    recentDonorDonations,
    recentVolunteers,
    upcomingEvents,
    combinedActivities: activityItems,
    performanceGoals,
    donationGrowth,
    donorGrowth,
    volunteerGrowth,
    isLoading: loading || donationsLoading || donorsLoading,
    error
  };
}; 